import { faPenToSquare, faRightFromBracket, faRightToBracket, faUserClock } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import type { ListItem } from "../../components/List/List"
import { companyMemory } from "../Memory/Memory"
import { formatDateTimeInTimeZone, formatTimeInTimeZone } from "../../utils/timeZone"
import type { TimeClockAdjustment } from "./TimeClockAdjustment"
import type { TimeClockLiveEmployee } from "./TimeClockLiveEmployee"
import type { TimeClockEmployeeSummary } from "./TimeClockEmployeeSummary"
import type { TimeClockEntry } from "./TimeClockEntry"
import { formatWorkedMinutes } from "./TimeClockSettings"

export type TimeClockTranslate = (key: string, options?: Record<string, unknown>) => string

const buildCardShell = (
  isEnabled: boolean,
  icon: IconProp,
  iconClassName: string,
  title: string,
  description: string,
  statusLabel: string,
  statusClassName: string,
  highlight: string,
  tags: { label: string; className: string }[],
) => (
  <div
    className={`
          flex flex-col gap-3
          p-4
          rounded-2xl
          transition-all duration-300
          ${isEnabled ? "bg-gradient-to-r from-orange-50 to-white hover:shadow-lg hover:scale-[1.01]" : "bg-gray-100 opacity-70"}
        `}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex gap-3 min-w-0 flex-1">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconClassName}`}>
          <FontAwesomeIcon icon={icon} />
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-base font-extrabold text-gray-900 truncate">{title}</span>

          <p className="text-sm text-gray-700 line-clamp-2 font-medium">{description}</p>
        </div>
      </div>

      <span className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap ${statusClassName}`}>
        {statusLabel}
      </span>
    </div>

    <div className="border-t border-gray-200 pt-3 flex items-center justify-between gap-3">
      <span className="text-2xl font-extrabold text-orange-700 whitespace-nowrap">{highlight}</span>

      <div className="flex flex-wrap gap-2 justify-end">
        {tags.map((tag) => (
          <span key={tag.label} className={`px-2 py-1 text-xs rounded-full font-semibold ${tag.className}`}>
            {tag.label}
          </span>
        ))}
      </div>
    </div>
  </div>
)

export const getTimeClockEmployeeCard = (
  employee: TimeClockEmployeeSummary,
  live: TimeClockLiveEmployee | undefined,
  index: number,
  t: TimeClockTranslate,
  language: string,
): ListItem => {
  const isWorking = live?.isWorking ?? false

  const scheduledDays = employee.schedules.filter((schedule) => !schedule.isDayOff).length

  const tags: { label: string; className: string }[] = [
    {
      label: employee.hasPin ? t("management.has_pin") : t("management.without_pin"),
      className: employee.hasPin ? "bg-cyan-100 text-cyan-700" : "bg-gray-100 text-gray-600",
    },
    {
      label:
        scheduledDays === 0
          ? t("management.without_schedule")
          : scheduledDays === 1
            ? t("management.schedule_days_one", { count: scheduledDays })
            : t("management.schedule_days_other", { count: scheduledDays }),
      className: scheduledDays > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600",
    },
  ]

  if (live && live.pendingCount > 0) {
    tags.push({
      label:
        live.pendingCount === 1
          ? t("management.pending_entries_one", { count: live.pendingCount })
          : t("management.pending_entries_other", { count: live.pendingCount }),
      className: "bg-red-100 text-red-700",
    })
  }

  if (live?.lastPunchAtUtc) {
    tags.push({
      label: `${t("management.last_punch")} ${formatTimeInTimeZone(live.lastPunchAtUtc, language, companyMemory.timeZoneId)}`,
      className: "bg-slate-100 text-slate-700",
    })
  }

  return {
    id: index,
    index,
    isDisable: !isWorking,
    title: employee.employeeName,
    linkImage: employee.iconUrl,
    dataFilter: [
      employee.employeeName,
      employee.companyFunction,
      isWorking ? t("management.working_now") : t("management.not_working"),
      employee.hasPin ? t("management.has_pin") : t("management.without_pin"),
    ],
    children: buildCardShell(
      isWorking,
      faUserClock,
      "bg-orange-200 text-orange-700",
      employee.employeeName,
      employee.companyFunction || t("management.no_function"),
      isWorking ? t("management.working_now") : t("management.not_working"),
      isWorking ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600",
      formatWorkedMinutes(live?.workedMinutes ?? 0),
      tags,
    ),
  }
}

export const getTimeClockEntryCard = (
  entry: TimeClockEntry,
  index: number,
  t: TimeClockTranslate,
  language: string,
  showEmployee = false,
): ListItem => {
  const isIn = entry.direction === "In"

  const isRejected = entry.status === "Rejected"

  const isPending = entry.status === "Pending"

  const tags: { label: string; className: string }[] = [
    {
      label: t(
        `common.origin_${entry.origin === "EmployeeDevice" ? "employee" : entry.origin === "SharedTerminal" ? "terminal" : "manager"}`,
      ),
      className: "bg-slate-100 text-slate-700",
    },
  ]

  if (entry.distanceMeters != null) {
    tags.push({
      label: t("common.distance", { meters: Math.round(entry.distanceMeters) }),
      className: "bg-cyan-100 text-cyan-700",
    })
  }

  const hiddenFailureReasons = t("common.hidden_failure_reasons", { returnObjects: true }) as unknown as string[]

  if (entry.failureReason && !hiddenFailureReasons.includes(entry.failureReason)) {
    tags.push({ label: entry.failureReason, className: "bg-red-100 text-red-700" })
  }

  if (entry.adjustmentReason) {
    tags.push({ label: t("common.from_adjustment"), className: "bg-purple-100 text-purple-700" })
  }

  return {
    id: entry.id,
    index,
    isDisable: isRejected,
    title: isIn ? t("common.direction_in") : t("common.direction_out"),
    linkImage: "",
    dataFilter: [
      isIn ? t("common.direction_in") : t("common.direction_out"),
      entry.employeeName,
      t(`common.status_${entry.status.toLowerCase()}`),
      formatDateTimeInTimeZone(entry.occurredAtUtc, language, companyMemory.timeZoneId),
    ],
    children: buildCardShell(
      !isRejected,
      isIn ? faRightToBracket : faRightFromBracket,
      isIn ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800",
      isIn ? t("common.direction_in") : t("common.direction_out"),
      showEmployee && entry.employeeName
        ? `${entry.employeeName} · ${formatDateTimeInTimeZone(entry.occurredAtUtc, language, companyMemory.timeZoneId)}`
        : formatDateTimeInTimeZone(entry.occurredAtUtc, language, companyMemory.timeZoneId),
      t(`common.status_${entry.status.toLowerCase()}`),
      isPending
        ? "bg-amber-100 text-amber-800"
        : isRejected
          ? "bg-gray-200 text-gray-600"
          : "bg-green-100 text-green-700",
      formatTimeInTimeZone(entry.occurredAtUtc, language, companyMemory.timeZoneId),
      tags,
    ),
  }
}

export const getTimeClockAdjustmentCard = (
  adjustment: TimeClockAdjustment,
  index: number,
  t: TimeClockTranslate,
  language: string,
): ListItem => {
  const isPending = adjustment.status === "Pending"

  const tags: { label: string; className: string }[] = [
    {
      label: t(`adjustment.type_${adjustment.requestedType.toLowerCase()}`),
      className: "bg-amber-100 text-amber-700",
    },
  ]

  if (adjustment.requestedDirection) {
    tags.push({
      label: adjustment.requestedDirection === "In" ? t("common.direction_in") : t("common.direction_out"),
      className: "bg-slate-100 text-slate-700",
    })
  }

  return {
    id: adjustment.id,
    index,
    isDisable: !isPending,
    title: adjustment.employeeName,
    linkImage: "",
    dataFilter: [adjustment.employeeName, adjustment.reason, t(`common.adjustment_${adjustment.status.toLowerCase()}`)],
    children: buildCardShell(
      isPending,
      faPenToSquare,
      "bg-purple-200 text-purple-800",
      adjustment.employeeName,
      adjustment.reason,
      t(`common.adjustment_${adjustment.status.toLowerCase()}`),
      isPending
        ? "bg-amber-100 text-amber-800"
        : adjustment.status === "Approved"
          ? "bg-green-100 text-green-700"
          : "bg-gray-200 text-gray-600",
      adjustment.requestedOccurredAtUtc
        ? formatTimeInTimeZone(adjustment.requestedOccurredAtUtc, language, companyMemory.timeZoneId)
        : "--:--",
      tags,
    ),
  }
}
